import { useDispatch, useSelector } from "react-redux"
import {
    usePost,
} from "../../api/reactQueryCustomHooks"
import { apiRoute, GetLoginCredentialDb, linksUrl } from "../../constants/enum"
import {
    ErrorCard,
    LoadingCard,
    MainContainer,
    MasterGrid,
    NextUITable,
} from "../../components"
import {
    Button, Select, SelectItem
} from "@nextui-org/react"
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from "dayjs"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { format } from "date-fns";
import { useCallback } from "react"
import { faInfo, faSearch, faServer } from "@fortawesome/free-solid-svg-icons"
import { formatCurrency } from "../../Utility/utility"

const BalanceSheetReport = () => {
    let navigate = useNavigate()
    const { token, user } = useSelector((state) => state.auth)
    const { compCode, compName, isMultipleCompany, startDate, endDate, yearId } = useSelector((state) => state.company)
    const frDate = (date) => (date ? format(new Date(date), "yyyy-MM-dd") : null);
    const [billDate, setBillDate] = useState({
        fromDate: frDate(startDate) || null,
        toDate: frDate(endDate) || null
    })
    const navi = useNavigate();
    const [selRptFormat, setSelRptFormat] = useState("LAS")


    useEffect(() => {
        if (isMultipleCompany)
            navi(linksUrl.dashboard.tradingDashboard)
    }, [])


    const {
        isError,
        isPending,
        data = [],
        mutate: filterData
    } = usePost({
        url: apiRoute.transaction.creditdebitnote.getBalanceSheetData,
        queryKey: "Credit Trial Balance Details",
    })

    //console.log(GetLoginCredentialDb())
    const funFilterData = () => {
        filterData({
            data: {
                compcode: compCode.join(","),
                from_date: billDate?.fromDate,
                to_date: billDate?.toDate,
            }
        })
    }
    const finalData = data.reduce((acc, item) => {
        const type = (item.DataType || "").toLowerCase();
        const group = item.Grp_Desc;

        if (!acc[type]) acc[type] = {};
        if (!acc[type][group]) acc[type][group] = [];

        acc[type][group].push({
            desc: item.Ac_Desc,
            amount: Math.abs(item.Balance)
        });

        return acc;
    }, { assets: {}, liabilities: {}, total: {} });

    // console.log(finalData?.total?.Income[0].amount)
    const { netProfit } = useMemo(() => {

        const income = Number(finalData?.total?.Income?.[0]?.amount ?? 0)
        const expense = Number(finalData?.total?.Expense?.[0]?.amount ?? 0)

        const netProfit = income - expense

        return {
            netProfit: isNaN(netProfit) ? 0 : netProfit
        }

    }, [finalData])

    const handleFilter = () => {
        funFilterData();
    }
    const handleClear = () => {
        setBillDate({
            fromDate: startDate || null,
            toDate: endDate || null
        })
        filterData({
            data: {
                compcode: compCode.join(","),
                from_date: startDate,
                to_date: endDate,
            }
        })
    }
    const getTotalRows = (groups) =>
        Object.values(groups).reduce((sum, rows) => sum + rows.length + 2, 0)
    const ass_totalRows = getTotalRows(finalData.assets)
    const lia_totalRows = getTotalRows(finalData.liabilities)

    const col_1Width = ' w-[70%]'
    const col_2Width = 'w-[30%]'
    const border_class = 'border border-gray-500 text-xs'

    const renderBlankRow = useCallback((maxVal, minVal) => {
        return Array.from({ length: maxVal - minVal }).map((_, i) => (
            <tr key={i}>
                <td className={` ${border_class} px-2 py-1`}>-</td>
                <td className={` ${border_class} px-2 py-1`}>-</td>
            </tr>
        ))
    }, [])


    const getTotal = (obj) => {
        return Object.values(obj).reduce((categoryTotal, category) => {
            const catSum = category.reduce((sum, item) => sum + item.amount, 0);
            return categoryTotal + catSum;
        }, 0);
    };


    const {
        leftData,
        rightData,
        leftTitle,
        rightTitle,
        leftTotal,
        rightTotal,
        leftRows,
        rightRows,
        showProfitLeft,
        showProfitRight,
        showLossLeft,
        showLossRight,
        shortLeftTitle,
        shortRightTitle
        ,
    } = useMemo(() => {

        const isLiabilityFirst = selRptFormat === "LAS";

        // Assign left/right data based on format
        const leftData = isLiabilityFirst ? finalData.liabilities : finalData.assets;
        const rightData = isLiabilityFirst ? finalData.assets : finalData.liabilities;

        // Titles
        const leftTitle = isLiabilityFirst ? "Liabilities" : "Assets";
        const rightTitle = isLiabilityFirst ? "Assets" : "Liabilities";
        const shortLeftTitle = isLiabilityFirst ? "LIA" : "ASS";
        const shortRightTitle = isLiabilityFirst ? "ASS" : "LIA";


        // Totals based on **current left/right data**
        const leftTotal = Object.values(leftData).reduce(
            (sum, group) => sum + group.reduce((s, item) => s + item.amount, 0),
            0
        );
        const rightTotal = Object.values(rightData).reduce(
            (sum, group) => sum + group.reduce((s, item) => s + item.amount, 0),
            0
        );

        // Row counts
        const leftRows = Object.values(leftData).reduce((sum, group) => sum + group.length + 2, 0);
        const rightRows = Object.values(rightData).reduce((sum, group) => sum + group.length + 2, 0);

        // NET PROFIT / LOSS display
        const showProfitLeft = isLiabilityFirst && netProfit > 0;
        const showProfitRight = !isLiabilityFirst && netProfit > 0;

        const showLossLeft = !isLiabilityFirst && netProfit < 0;
        const showLossRight = isLiabilityFirst && netProfit < 0;

        return {
            leftData,
            rightData,
            leftTitle,
            rightTitle,
            leftTotal,
            rightTotal,
            leftRows,
            rightRows,
            showProfitLeft,
            showProfitRight,
            showLossLeft,
            showLossRight,
            shortLeftTitle,
            shortRightTitle
        };
    }, [finalData, selRptFormat, netProfit]);


    // console.log(totalLiabilities, totalAssets, "fg")
    useEffect(() => {
        const leftRows = document.querySelectorAll(".left-table tr");
        const rightRows = document.querySelectorAll(".right-table tr");


        const max = Math.max(leftRows.length, rightRows.length);

        for (let i = 0; i < max; i++) {
            const l = leftRows[i];
            const r = rightRows[i];

            if (l && r) {
                const h = Math.max(l.offsetHeight, r.offsetHeight);
                l.style.height = `${h}px`;
                r.style.height = `${h}px`;
            }
        }

    }, [finalData]);

    const GroupTable = ({ groups = {}, col1Width = "w-[70%]", col2Width = "w-[30%]", grpType = "" }) => {

        const groupKeys = Object.keys(groups)

        return (
            <>
                <colgroup>
                    <col className={col1Width} />
                    <col className={col2Width} />
                </colgroup>

                {groupKeys.map((group, i) => {

                    const rows = groups[group] || []

                    const total = rows.reduce((sum, r) => sum + Math.abs(r.amount), 0)

                    return (
                        <Fragment key={i}>

                            {/* Group Header */}
                            <tr className="bg-gray-50 font-bold text-blue-700">
                                <td colSpan="2" className={` ${border_class} px-2 py-1 uppercase`} style={{ fontSize: "0.7rem" }} >
                                    {group || ""}
                                </td>
                            </tr>

                            {/* Rows */}
                            {rows.map((row, j) => (
                                <tr key={j}>
                                    <td className={` ${border_class} px-2 py-1 uppercase truncate`} style={{ fontSize: "0.7rem" }}>
                                        {row?.desc || ""}
                                    </td>
                                    <td className={` ${border_class} px-2 py-1  text-right`}>
                                        {formatCurrency(row?.amount) || ""}
                                    </td>
                                </tr>
                            ))}

                            {/* Group Total */}
                            <tr className="font-bold bg-graye-50 text-purple-700">
                                <td className={` ${border_class} px-2 py-1 text-right`} style={{ fontSize: "0.7rem" }}>
                                    {group ? `TOTAL ${group} :` : ""}
                                </td>
                                <td className={` ${border_class} px-2 py-1 text-right`}>
                                    {formatCurrency(total)}
                                </td>
                            </tr>

                        </Fragment>
                    )
                })}
            </>
        )
    }
    const grpHead_class = 'uppercase bg-orange-100  text-slate-600  text-center font-bold'
    const head_class = '  uppercase bg-cyan-600  text-white  text-center font-bold'

    // Main export function


    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-GB");
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Balance Sheet");
        const _fontSize = 10
        worksheet.columns = [
            { width: 45 },
            { width: 18 },
            { width: 45 },
            { width: 18 }
        ];

        // Header fills
        const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0891b2' } };
        const subHeaderFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ffedd5' } };
        const groupFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F6F6F6' } };
        const totalFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ffffff' } };

        // ---------------- HEADER ----------------
        worksheet.mergeCells("A1:D1");
        worksheet.getCell("A1").value = compName;
        worksheet.getCell("A1").font = { bold: true, size: 14 };
        worksheet.getCell("A1").alignment = { horizontal: "center" };

        worksheet.mergeCells("A2:D2");
        worksheet.getCell("A2").value = `BALANCE SHEET REPORT FROM ${formatDate(billDate.fromDate)} TO ${formatDate(billDate.toDate)}`;
        worksheet.getCell("A2").font = { bold: true, size: 10 };
        worksheet.getCell("A2").alignment = { horizontal: "center" };

        worksheet.mergeCells("A3:D3");
        worksheet.getCell("A3").value = `Generated On :${formatDate(new Date())}`;
        worksheet.getCell("A3").font = { size: 8 };
        worksheet.getCell("A3").alignment = { horizontal: "right" };

        /// worksheet.addRow([]);

        // ---------------- COLUMN HEADER ----------------
        const headerRow = worksheet.addRow([
            "Group / Account Desc.",
            `Amount(${shortLeftTitle})`,
            "Group / Account Desc.",
            `Amount(${shortRightTitle})`
        ]);
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.alignment = { horizontal: "center" };
        headerRow.eachCell(cell => { cell.fill = headerFill; });

        const subHeaderRow = worksheet.addRow([leftTitle, "", rightTitle, ""]);
        subHeaderRow.font = { bold: true };
        subHeaderRow.alignment = { horizontal: "center" };
        subHeaderRow.eachCell(cell => { cell.fill = subHeaderFill; });

        // ---------------- FORMAT FUNCTION ----------------
        const formatAmt = (num) => {
            const number = Number(num);
            if (isNaN(number)) return "";
            return number.toLocaleString("en-IN");
        };

        // ---------------- DATA ROWS ----------------
        const addSideData = (groups) => {
            const rows = [];
            Object.keys(groups).forEach(group => {
                // Group Header (bold + bg)
                rows.push({ desc: group, amount: "-", bold: true, fill: groupFill, size: _fontSize });

                // Accounts (amount right-aligned)
                groups[group].forEach(acc => {
                    rows.push({ desc: acc.desc, amount: formatAmt(acc.amount), horizontal: "right", fontSize: _fontSize });
                });

                // Group Total (desc + amount right-aligned)
                const total = groups[group].reduce((s, r) => s + r.amount, 0);
                rows.push({
                    desc: `TOTAL ${group}:`,
                    amount: formatAmt(total),
                    bold: true,
                    fontSize: _fontSize,
                    horizontal: "right", // amount right
                    descAlign: "right",  // description right
                    fill: totalFill
                });
            });
            return rows;
        };

        const leftRowsData = addSideData(leftData);
        const rightRowsData = addSideData(rightData);

        const maxRows = Math.max(leftRowsData.length, rightRowsData.length);

        for (let i = 0; i < maxRows; i++) {

            const row = worksheet.addRow([
                leftRowsData[i]?.desc || "",
                leftRowsData[i]?.amount || "",
                rightRowsData[i]?.desc || "",
                rightRowsData[i]?.amount || ""
            ]);
            row.font = { size: _fontSize }
            // --- LEFT SIDE ---
            if (leftRowsData[i]?.bold) row.getCell(1).font = { bold: true, size: _fontSize };
            if (leftRowsData[i]?.bold) row.getCell(2).font = { bold: true, size: _fontSize };
            if (leftRowsData[i]?.fill) {
                row.getCell(1).fill = leftRowsData[i].fill;
                row.getCell(2).fill = leftRowsData[i].fill;
            }
            // Amount always right
            row.getCell(2).alignment = { horizontal: leftRowsData[i]?.horizontal || "right" };
            // TOTAL description right
            if (leftRowsData[i]?.descAlign) row.getCell(1).alignment = { horizontal: leftRowsData[i].descAlign };
            if (leftRowsData[i]?.descAlign) row.getCell(1).font = { color: { argb: "a30a96" }, bold: true, size: _fontSize };
            if (leftRowsData[i]?.descAlign) row.getCell(2).font = { color: { argb: "a30a96" }, bold: true, size: _fontSize };

            // --- RIGHT SIDE ---
            if (rightRowsData[i]?.bold) row.getCell(3).font = { bold: true, size: _fontSize };
            if (rightRowsData[i]?.bold) row.getCell(4).font = { bold: true, size: _fontSize };
            if (rightRowsData[i]?.fill) {
                row.getCell(3).fill = rightRowsData[i].fill;
                row.getCell(4).fill = rightRowsData[i].fill;
            }
            // Amount always right
            row.getCell(4).alignment = { horizontal: rightRowsData[i]?.horizontal || "right" };
            // TOTAL description right
            if (rightRowsData[i]?.descAlign) row.getCell(3).alignment = { horizontal: rightRowsData[i].descAlign };
            if (rightRowsData[i]?.descAlign) row.getCell(3).font = { color: { argb: "a30a96" }, bold: true, size: _fontSize };
            if (rightRowsData[i]?.descAlign) row.getCell(4).font = { color: { argb: "a30a96" }, bold: true, size: _fontSize };
        }

        // ---------------- NET PROFIT / LOSS ----------------
        const profitLossRow = worksheet.addRow([
            showProfitLeft ? "NET PROFIT" : showLossLeft ? "NET LOSS" : "",
            (showProfitLeft || showLossLeft) ? formatAmt(Math.abs(netProfit)) : "",
            showProfitRight ? "NET PROFIT" : showLossRight ? "NET LOSS" : "",
            (showProfitRight || showLossRight) ? formatAmt(Math.abs(netProfit)) : ""
        ]);
        profitLossRow.eachCell(cell => { cell.font = { bold: true, size: _fontSize }; cell.fill = subHeaderFill; });
        // Align amounts right
        profitLossRow.getCell(2).alignment = { horizontal: "right" };
        profitLossRow.getCell(4).alignment = { horizontal: "right" };

        // ---------------- TOTAL ROWS ----------------
        const finalLeftTotal = showLossLeft || showProfitLeft ? leftTotal + Math.abs(netProfit) : leftTotal;
        const finalRightTotal = showProfitRight || showLossRight ? rightTotal + Math.abs(netProfit) : rightTotal;

        const totalRow = worksheet.addRow([
            `TOTAL OF ${leftTitle.toUpperCase()}`,
            formatAmt(finalLeftTotal),
            `TOTAL OF ${rightTitle.toUpperCase()}`,
            formatAmt(finalRightTotal)
        ]);
        totalRow.eachCell(cell => { cell.font = { bold: true, size: _fontSize, color: { argb: 'FFFFFFFF' } }; cell.fill = headerFill; });
        totalRow.getCell(2).alignment = { horizontal: "right" };
        totalRow.getCell(4).alignment = { horizontal: "right" };
        // Optional: make TOTAL description also right
        totalRow.getCell(1).alignment = { horizontal: "right" };
        totalRow.getCell(3).alignment = { horizontal: "right" };

        // ---------------- BORDER ----------------
        worksheet.eachRow(row => {
            row.eachCell(cell => {
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" }
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer]),
            `BalanceSheet_${billDate.fromDate}_${billDate.toDate}.xlsx`
        );
    };
    const exportToPDF = () => {
        const doc = new jsPDF("p", "mm", "a4");

        const pageHeader = (data) => {
            doc.setFontSize(12);
            doc.setFont(undefined, "bold");
            doc.text(compName, 105, 10, { align: "center" });

            doc.setFontSize(10);
            doc.setFont(undefined, "bold");
            doc.text(`BALANCE SHEET REPORT FROM ${formatDate(billDate.fromDate)} TO ${formatDate(billDate.toDate)}`, 105, 16,
                { align: "center" });

            // doc.setFontSize(9);
            // doc.text(
            //     `From ${formatDate(billDate.fromDate)} To ${formatDate(billDate.toDate)}`,
            //     105,
            //     22,
            //     { align: "center" }
            // );
        };

        // PREPARE DATA
        const addSideData = (groups) => {
            const rows = [];
            Object.keys(groups).forEach(group => {

                rows.push([
                    { content: group, styles: { fontStyle: "bold" } },
                    ""
                ]);

                groups[group].forEach(acc => {
                    rows.push([acc.desc, formatCurrency(acc.amount)]);
                });

                const total = groups[group].reduce((s, r) => s + r.amount, 0);

                rows.push([
                    { content: `TOTAL ${group}:`, styles: { fontStyle: "bold", halign: "right" } },
                    { content: formatCurrency(total), styles: { fontStyle: "bold", fontSize: 8 } }
                ]);

            });

            return rows;
        };

        const leftRows = addSideData(leftData);
        const rightRows = addSideData(rightData);

        const maxRows = Math.max(leftRows.length, rightRows.length);

        const tableRows = [];
        tableRows.push([
            {
                content: leftTitle.toUpperCase(),
                colSpan: 2,
                styles: {
                    halign: "center",
                    fontStyle: "bold",
                    fontSize: 8,
                    fillColor: [255, 237, 213]
                }
            },
            {
                content: rightTitle.toUpperCase(),
                colSpan: 2,
                styles: {
                    halign: "center",
                    fontStyle: "bold",
                    fontSize: 8,
                    fillColor: [255, 237, 213]
                }
            }
        ]);

        for (let i = 0; i < maxRows; i++) {
            tableRows.push([
                leftRows[i]?.[0] || "",
                leftRows[i]?.[1] || "",
                rightRows[i]?.[0] || "",
                rightRows[i]?.[1] || ""
            ]);
        }

        // NET PROFIT / LOSS
        tableRows.push([
            showProfitLeft ? "NET PROFIT" : showLossLeft ? "NET LOSS" : "",
            (showProfitLeft || showLossLeft) ? formatCurrency(Math.abs(netProfit)) : "",
            showProfitRight ? "NET PROFIT" : showLossRight ? "NET LOSS" : "",
            (showProfitRight || showLossRight) ? formatCurrency(Math.abs(netProfit)) : ""
        ]);

        // FINAL TOTALS
        const finalLeftTotal =
            showLossLeft || showProfitLeft
                ? leftTotal + Math.abs(netProfit)
                : leftTotal;

        const finalRightTotal =
            showProfitRight || showLossRight
                ? rightTotal + Math.abs(netProfit)
                : rightTotal;

        tableRows.push([
            `TOTAL OF ${leftTitle.toUpperCase()}`,
            formatCurrency(finalLeftTotal),
            `TOTAL OF ${rightTitle.toUpperCase()}`,
            formatCurrency(finalRightTotal)
        ]);

        // TABLE
        autoTable(doc, {
            startY: 20,
            head: [["Group / Account Desc.", `Amount(${shortLeftTitle})`, "Group / Account Desc.", `Amount(${shortRightTitle})`]],
            body: tableRows,
            theme: "grid",

            headStyles: {
                fillColor: [8, 145, 178],
                halign: "center",
                fontSize: 8
            },
            styles: {
                fontSize: 7
            },
            columnStyles: {
                0: {},
                1: { halign: "right" },
                2: {},
                3: { halign: "right" }
            },

            didDrawPage: function (data) {
                data.settings.margin.top = 20
                pageHeader(data); // repeat header every page
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                doc.setFontSize(9);
                doc.text(
                    `Generated on: ${formatDate(new Date())}`,
                    14,
                    pageHeight - 10
                );
                // 🔴 Page Number
                const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();

                doc.setFontSize(9);
                doc.text(
                    `Page ${pageNumber} of ${totalPages}`,
                    pageWidth - 20,
                    pageHeight - 10
                );
            },

            didParseCell: function (data) {

                const rowIndex = data.row.index;

                // Net profit row
                if (rowIndex === tableRows.length - 2) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.fillColor = [255, 237, 213];
                }

                // Total row
                if (rowIndex === tableRows.length - 1) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.fillColor = [8, 145, 178];
                    data.cell.styles.textColor = [255, 255, 255];
                }
            }

        });

        doc.save(`BalanceSheet_${billDate.fromDate}_${billDate.toDate}.pdf`);
    };

    if (isError) {
        return <ErrorCard />
    }

    if (isPending) {
        return <LoadingCard />
    }
    return (
        <div>


            <MainContainer
                pageName="Balance Sheet Report"
                info={[]}
                breadcrumbs={["Reports & view","Final Reports", "Balance Sheet Report"]}
            >
                <div className="p-2 shadow mb-5">
                    {/* <div className="pb-1 text-sm font-semibold">
                        Inward Date
                    </div> */}
                    <div className="flex  flex-wrap sm:flex-nowrap md:flex-nowrap justify-center items-center gap-5 ">

                        <Select
                            selectedKeys={[selRptFormat]}
                            variant="bordered"
                            color="warning"
                            size="sm"
                            label="FORMAT "
                            //labelPlacement="outside"
                            onChange={(e) => setSelRptFormat(e.target.value)}
                            classNames={{ label: "text-sm py-1" }}
                            className="w-full sm:w-48 md:w-48 text-xs  font-semibold"
                        >
                            <SelectItem key="LAS" className="text-xs">LIABILITY_ASSESTS</SelectItem>
                            <SelectItem key="ASL">ASSESTS_LIABILITY</SelectItem>
                        </Select>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={"From Date"}
                                views={["day", "month", "year"]}
                                size="small"
                                format="DD/MM/YYYY"
                                className="w-full sm:w-40 md:w-40"
                                value={
                                    billDate.fromDate
                                        ? dayjs(billDate.fromDate)
                                        : billDate.fromDate
                                }
                                onChange={(newValue) => {
                                    setBillDate((prev) => {
                                        return {
                                            ...prev,
                                            fromDate: newValue != '' && newValue != 'Invalid Date' ?
                                                dayjs(newValue).format("YYYY-MM-DD") : null,
                                        };
                                    });
                                }}
                                slotProps={{
                                    textField: { size: "small", },
                                }}
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={"To  Date"}
                                views={["day", "month", "year"]}
                                format="DD/MM/YYYY"
                                size="small"
                                className="w-full sm:w-40 md:w-40"
                                value={
                                    billDate.toDate
                                        ? dayjs(billDate.toDate)
                                        : billDate.toDate
                                }
                                onChange={(newValue) => {
                                    setBillDate((prev) => {
                                        return {
                                            ...prev,
                                            toDate: newValue != '' && newValue != 'Invalid Date' ?
                                                dayjs(newValue).format("YYYY-MM-DD") : null,
                                        };
                                    });
                                }}
                                slotProps={{
                                    textField: { size: "small", },
                                }}
                            />
                        </LocalizationProvider>

                        <div className="flex no-wrap gap-5 justify-center items-center">
                            <Button onPress={handleFilter}
                                startContent={<FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>}
                                className="font-semibold uppercase bg-sky-700 text-white" variant="solid" color="primary" size="sm" >Find</Button>
                            <Button onPress={handleClear} className="font-semibold uppercase " variant="solid" color="danger" size="sm" >Reset Date</Button>
                        </div>
                    </div>
                </div>
                {data && data.length > 0 ?
                    <div className="p-4">

                        <div className="flex gap-2 justify-end">
                            <button onClick={exportToExcel}
                                className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                            >
                                Export Excel
                            </button>
                            <button onClick={exportToPDF} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Export PDF</button>
                        </div>
                        <table className="w-full border border-slate-700 table-fixed border-collapse text-xs font-medium">
                            <colgroup>
                                <col className={col_1Width} />
                                <col className={col_2Width} />
                                <col className={col_1Width} />
                                <col className={col_2Width} />
                            </colgroup>
                            <thead>
                                <tr className={`${head_class}`}>
                                    <th className={` ${border_class}  px-2 py-1 text-left `}>Group / Account Desc.</th>
                                    <th className={` ${border_class}  px-2 py-1 text-right`}>{`Amount(${shortLeftTitle})`} </th>
                                    <th className={` ${border_class}  px-2 py-1 text-left `}>Group / Account Desc.</th>
                                    <th className={` ${border_class}  px-2 py-1 text-right `}>{`Amount(${shortRightTitle})`} </th>
                                </tr>
                                <tr className={`${grpHead_class}`}>

                                    <th colSpan="2" className={`${border_class} py-1`}>{leftTitle}</th>
                                    <th colSpan="2" className={`${border_class} py-1`}>{rightTitle}</th>
                                </tr>
                            </thead>

                            <tbody>

                                <tr>
                                    <td colSpan={2} className="align-top">
                                        <table className="w-full table-fixed left-table">
                                            <GroupTable groups={leftData} />
                                            {rightRows > leftRows ? renderBlankRow(rightRows, leftRows) : ""}

                                            <tr>
                                                <td className={` ${border_class}  px-2 py-1 text-right`}>-</td>
                                                <td className={` ${border_class}   px-2 py-1 text-right`}>-</td>

                                            </tr>
                                            {/* <tr className={`${grpHead_class}`}>
                                                <td className={` ${border_class}  px-2 py-1 text-right`}></td>
                                                <td className={` ${border_class}   px-2 py-1 text-right`}>{formatNumber(leftTotal)}</td>

                                            </tr> */}
                                            {/* Trading Account Totals */}

                                            <tr className={grpHead_class}>
                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {showProfitLeft && "NET PROFIT"}
                                                    {showLossLeft && "NET LOSS"}
                                                    {!showProfitLeft && !showLossLeft && "-"}
                                                </td>

                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {(showProfitLeft || showLossLeft)
                                                        ? formatCurrency(Math.abs(netProfit))
                                                        : "-"
                                                    }
                                                </td>
                                            </tr>

                                            <tr className={`${head_class}`}>
                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {` TOTAL OF ${leftTitle} : `}
                                                </td>
                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {showProfitLeft
                                                        ? formatCurrency(leftTotal + Math.abs(netProfit)) // net profit on left
                                                        : showLossLeft
                                                            ? formatCurrency(leftTotal + Math.abs(netProfit)) // net loss on left
                                                            : formatCurrency(leftTotal)
                                                    }
                                                </td>
                                            </tr>

                                            {/* Gross Profit / Loss */}


                                        </table>
                                    </td>
                                    <td colSpan={2} className="align-top">
                                        <table className="w-full table-fixed  right-table">
                                            <GroupTable groups={rightData} />
                                            {leftRows > rightRows ? renderBlankRow(leftRows, rightRows) : ""}
                                            <tr>
                                                <td className={` ${border_class}  px-2 py-1 text-right`}>-</td>
                                                <td className={` ${border_class}   px-2 py-1 text-right`}>-</td>

                                            </tr>
                                            {/* <tr className={`${grpHead_class}`}>
                                                <td className={` ${border_class}  px-2 py-1 text-right`}>Total</td>
                                                <td className={` ${border_class}   px-2 py-1 text-right`}>{formatNumber(rightTotal)}</td>

                                            </tr> */}
                                            <tr className={grpHead_class}>
                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {showProfitRight && "NET PROFIT"}
                                                    {showLossRight && "NET LOSS"}
                                                    {!showProfitRight && !showLossRight && "-"}
                                                </td>

                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {(showProfitRight || showLossRight)
                                                        ? formatCurrency(Math.abs(netProfit))
                                                        : "-"
                                                    }
                                                </td>
                                            </tr>

                                            <tr className={`${head_class}`}>
                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {` TOTAL OF ${rightTitle} : `}
                                                </td>
                                                <td className={`${border_class} px-2 py-1 text-right`}>
                                                    {showProfitRight
                                                        ? formatCurrency(rightTotal + Math.abs(netProfit)) // net profit added on right
                                                        : showLossRight
                                                            ? formatCurrency(rightTotal + Math.abs(netProfit)) // net loss added on right
                                                            : formatCurrency(rightTotal)
                                                    }
                                                </td>
                                            </tr>

                                            {/* Gross Profit / Loss */}
                                            {/* {grossProfit < 0 ? (
                                                <tr className={head_class}>

                                                    <td className={` ${border_class}    px-2 py-1 text-right`}>GROSS LOSS</td>
                                                    <td className={` ${border_class}   px-2 py-1 text-right`}>{Math.abs(grossProfit).toLocaleString()}</td>
                                                </tr>
                                            ) : <tr className={head_class}>
                                                <td className={` ${border_class}  px-2 py-1`}>-</td>
                                                <td className={` ${border_class}   px-2 py-1 text-right`}>-</td>

                                            </tr>} */}

                                        </table>

                                    </td>
                                </tr>                   {/* --- PROFIT & LOSS ACCOUNT --- */}




                            </tbody>

                        </table>

                    </div>
                    :
                    !isPending && (
                        <div className="p-4 border-1 text-center"

                        >
                            <div className="flex justify-center  items-center flex-column gap-2"                 >
                                <FontAwesomeIcon icon={faInfo} />
                                {/* <DescriptionOutlined sx={{
                  fontSize: 48,
                  color: 'text.disabled'
                }} /> */}
                                <h6 className="text-slate-500">
                                    No  data available
                                </h6>
                                <h6 className="text-slate-500">
                                    Apply filters above to generate a report
                                </h6>
                            </div>
                        </div>
                    )}


            </MainContainer>
        </div>
    )
}
export default BalanceSheetReport


{/* <tr className="font-bold ">
                                            <td className="border border-black px-2 py-1">{grossProfit < 0 ? "GROSS LOSS" : "-"}</td>
                                            <td className="border border-black px-2 py-1 text-right">{grossProfit < 0 ? grossProfit.toLocaleString() : "-"}</td>
                                        </tr>
                                        {(() => {
                                            const expenseGroups = Object.keys(other.tradingData.Expense);
                                            const maxGroups = expenseGroups.length;

                                            return [...Array(maxGroups)].map((_, i) => {
                                                const expenseGrp = expenseGroups[i];
                                                const expenseRows = other.tradingData.Expense[expenseGrp] || [];
                                                const maxRows = expenseRows.length;
                                                const expenseTotal = expenseRows.reduce((s, r) => s + r.amount, 0);

                                                return (
                                                    <Fragment key={i}>
                                                        
                                                        <tr className="bg-gray-50 font-bold">
                                                            <td colSpan="2" className="border border-black px-2 py-1 font-bold">{expenseGrp || ""}</td>
                                                        </tr>

                                                       
                                                        {[...Array(maxRows)].map((_, j) => (
                                                            <tr key={j}>

                                                                <td className="border border-black px-2 py-1">{expenseRows[j]?.desc || ""}</td>
                                                                <td className="border border-black px-2 py-1 text-right">{expenseRows[j]?.amount?.toLocaleString() || ""}</td>
                                                            </tr>
                                                        ))}

                                                        
                                                        <tr className="font-bold bg-gray-100">

                                                            <td className="border border-black px-2 py-1 text-right">{expenseGrp ? expenseGrp + " TOTAL" : ""}</td>
                                                            <td className="border border-black px-2 py-1 text-right">{expenseTotal.toLocaleString()}</td>
                                                        </tr>
                                                    </Fragment>
                                                );
                                            });
                                        })()} */}